// The keypad (Stage 4.8f.4a, mockup "Softphone keypad"): type or paste a name's number; what it is shows as it is typed — the colleague
// or the customer it is, allowed or why not (checked before anyone's phone rings: server/ai/phoneDesk.js), the price a minute, the
// number the call will show (and the others it could show).
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, InputBase, Menu, MenuItem, Typography } from "@mui/material";
import { Delete, Phone, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { checkNumber, setCallerId } from "../pages/agent/phoneApi";
import { Chip, Dot, Initial } from "./phoneUi";

const KEYS = [["1", ""], ["2", "ABC"], ["3", "DEF"], ["4", "GHI"], ["5", "JKL"], ["6", "MNO"], ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"], ["*", ""], ["0", "+"], ["#", ""]];
const clean = (s) => String(s ?? "").replace(/[^\d+*#]/g, "");

export default function PhoneKeypad({ ready, onCall, prefill, prefillSeq }) {
  const { t, i18n } = useTranslation("phone");
  const [value, setValue] = useState("");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [menu, setMenu] = useState(null);
  const inputRef = useRef(null);
  const reqRef = useRef(0);

  // A number sent from elsewhere in the app (click-to-call), each time it is sent.
  useEffect(() => { if (prefill) setValue(clean(prefill)); }, [prefill, prefillSeq]);

  // The check, half a second after the last key.
  const refresh = (v) => {
    const n = clean(v);
    const my = ++reqRef.current;
    if (n.replace(/\D/g, "").length < 2) { setCheck(null); setChecking(false); return; }
    setChecking(true);
    checkNumber(n).then((r) => { if (my === reqRef.current) setCheck(r); })
      .catch(() => { if (my === reqRef.current) setCheck(null); })
      .finally(() => { if (my === reqRef.current) setChecking(false); });
  };
  useEffect(() => {
    const id = setTimeout(() => refresh(value), 450);
    return () => clearTimeout(id);
  }, [value]);

  const country = useMemo(() => {
    if (!check?.country) return null;
    try { return new Intl.DisplayNames([i18n.language], { type: "region" }).of(check.country); } catch { return check.country; }
  }, [check?.country, i18n.language]);

  const why = (r) => t(`why.${r}`, { defaultValue: t("why.other"), country: country ?? "" });
  const digits = clean(value);
  const blocked = check && check.ok === false && check.kind !== "none";
  const canCall = ready && digits.replace(/\D/g, "").length >= 2 && !blocked && !checking;
  const who = check?.kind === "extension" ? check.name : check?.contact?.name;

  const call = () => { if (canCall) onCall(digits, who ?? ""); };
  const press = (k) => { setValue((v) => v + k); inputRef.current?.focus(); };
  const pick = async (n) => {
    setMenu(null);
    try { await setCallerId(n); } catch { /* the check below shows what is used */ }
    refresh(value);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box sx={{ p: "16px 16px 0", display: "flex", flexDirection: "column", gap: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", height: 52, px: "10px", border: "2px solid var(--brand-violet)", borderRadius: "14px", background: "var(--surface-card)" }}>
          <InputBase inputRef={inputRef} value={value} onChange={(e) => setValue(clean(e.target.value))} onKeyDown={(e) => { if (e.key === "Enter") call(); }}
            placeholder={t("typeNumber")} inputProps={{ dir: "ltr", "aria-label": t("typeNumber"), inputMode: "tel", autoComplete: "off" }}
            sx={{ flex: 1, minWidth: 0, font: "700 22px/1 var(--font-ui)", letterSpacing: ".5px", color: "var(--ink-900)" }} />
          {value && (
            <ButtonBase onClick={() => setValue((v) => v.slice(0, -1))} aria-label={t("deleteDigit")}
              sx={{ width: 40, height: 40, borderRadius: "10px", color: "var(--ink-500)", "&:focus-visible": { outline: "2px solid var(--brand-violet)" } }}>
              <Delete size={20} strokeWidth={1.8} aria-hidden="true" />
            </ButtonBase>
          )}
        </Box>

        {/* Who it is */}
        {who && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px 10px", borderRadius: "12px", background: "var(--surface-2)" }}>
            <Initial name={who} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography dir="auto" noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{who}</Typography>
              <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>
                {check.kind === "extension" ? t("colleagueExt", { ext: check.ext }) + (check.away ? ` · ${t(`status.${check.away}`)}` : "") : t("customer")}
              </Typography>
            </Box>
            {check.kind !== "extension" && <Chip tone="violet">{t("inCrm")}</Chip>}
          </Box>
        )}

        {/* What it is, as typed */}
        {check && check.kind !== "none" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }} aria-live="polite">
            {check.ok ? (
              <Chip tone="ok"><Dot color="var(--success)" />
                {check.kind === "extension" ? t("kindColleague") : check.kind === "own_number" ? t("kindOwnNumber")
                  : `${t(check.local ? "kindLocal" : "kindInternational", { country: country ?? "" })}${check.numberType ? ` ${t(`numberType.${check.numberType}`, { defaultValue: "" })}` : ""} · ${t("allowed")}`}
              </Chip>
            ) : (
              <Chip tone="bad"><Dot color="var(--danger)" />{why(check.reason)}</Chip>
            )}
            {check.kind === "outside" && check.perMinTokens != null && check.ok && (
              <Chip>{t("perMinute", { tokens: Math.round(check.perMinTokens) })}</Chip>
            )}
            {check.kind === "outside" && check.shown && (
              <Chip onClick={check.numbers?.length > 1 ? (e) => setMenu(e.currentTarget) : undefined} title={t("shownTip")}>
                {t("shows")} <span dir="ltr">{check.shown}</span>{check.numbers?.length > 1 && <ChevronDown size={12} aria-hidden="true" />}
              </Chip>
            )}
            {check.creditAlert && <Chip tone="warn">{t("creditLow")}</Chip>}
          </Box>
        )}
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem onClick={() => pick(null)} selected={!check?.chosen}>{t("businessChoice")}</MenuItem>
          {(check?.numbers ?? []).map((n) => <MenuItem key={n} onClick={() => pick(n)} selected={check?.chosen === n}><span dir="ltr">{n}</span></MenuItem>)}
        </Menu>
      </Box>

      <Box role="group" aria-label={t("keypad")} sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px 14px", p: "16px 28px 8px", justifyItems: "center" }}>
        {KEYS.map(([d, l]) => (
          <ButtonBase key={d} onClick={() => press(d)} aria-label={l ? `${d} ${l}` : d}
            sx={{ width: 60, height: 60, borderRadius: "50%", border: "1px solid var(--line-100)", background: "var(--surface-2)", display: "flex", flexDirection: "column", gap: "1px",
              "&:hover": { background: "var(--line-100)" }, "&:focus-visible": { outline: "2px solid var(--brand-violet)", outlineOffset: 2 } }}>
            <Box component="span" sx={{ font: "600 22px/1 var(--font-ui)", color: "var(--ink-900)" }}>{d}</Box>
            <Box component="span" sx={{ font: "600 9px/1 var(--font-ui)", letterSpacing: "1.2px", color: "var(--ink-500)", minHeight: 9 }}>{l}</Box>
          </ButtonBase>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", p: "6px 16px 12px" }}>
        <ButtonBase onClick={call} disabled={!canCall} aria-label={who ? t("callName", { name: who }) : t("call")}
          sx={{ width: 64, height: 64, borderRadius: "50%", background: canCall ? "var(--grad-brand-135)" : "var(--line-200)", boxShadow: canCall ? "var(--shadow-cta)" : "none",
            color: "#fff", "&:focus-visible": { outline: "2px solid var(--brand-violet)", outlineOffset: 3 } }}>
          <Phone size={26} strokeWidth={1.9} aria-hidden="true" />
        </ButtonBase>
        <Typography sx={{ font: "600 12px/1 var(--font-ui)", color: "var(--ink-600)" }}>
          {who ? <>{t("call")} <span dir="auto">{who}</span></> : !ready ? t("notReady") : t("typeOrPaste")}
        </Typography>
      </Box>
    </Box>
  );
}
